import React, { type FunctionComponent, useCallback } from 'react'
import styled from '@emotion/styled'
import { Text, spacing, CloseIcon, LegalIcon, MarkerIcon, StoreIcon } from '@instacart/ids-core'
import { useDomainMessages } from 'domains/account-management/utils/domain/intl'
import { type OptionType } from 'domains/account-management/components/retailer-location-store-group-select/utils/types'

const typeToIcon: Record<OptionType, JSX.Element> = {
  storeGroup: <LegalIcon color="systemGrayscale30" size={20} />,
  retailerLocation: <MarkerIcon color="systemGrayscale30" size={20} />,
}

const StyledValuePill = styled.div(({ theme }) => ({
  borderRadius: theme.radius.r24,
  height: spacing.s32,
  padding: `${spacing.s4}px ${spacing.s12}px`,
  display: 'inline-block',
  backgroundColor: theme.colors.systemGrayscale10,
}))

const ContentContainer = styled.div({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
})

const TypeIconContainer = styled.div({
  display: 'flex',
  marginRight: spacing.s8,
})

const StyledCloseIcon = styled(CloseIcon)({
  display: 'flex',
  marginLeft: spacing.s16,
  cursor: 'pointer',
})

interface ValuePillProps {
  onRemove: (type: OptionType, value: string) => void
  label: string
  value: string
  type: OptionType
}

export const ValuePill: FunctionComponent<ValuePillProps> = ({ onRemove, label, type, value }) => {
  const messages = useDomainMessages({
    remove: 'accountManagementDomain.form.addStore.remove',
  })

  const handleOnRemove = useCallback(() => {
    onRemove(type, value)
  }, [onRemove, type, value])

  return (
    <StyledValuePill>
      <ContentContainer>
        <TypeIconContainer>
          {value === 'allStores' ? (
            <StoreIcon color="systemGrayscale30" size={20} />
          ) : (
            typeToIcon[type]
          )}
        </TypeIconContainer>
        <Text typography="bodyMedium1" color="systemGrayscale50">
          {label}
        </Text>
        <StyledCloseIcon
          role={'button'}
          onClick={handleOnRemove}
          size={20}
          color="systemGrayscale70"
          aria-label={`${messages.remove} ${label}`}
        />
      </ContentContainer>
    </StyledValuePill>
  )
}
